/**
 * RaceResultPage.jsx — Complete Race Analysis Result Page
 * ========================================================
 *
 * Standalone page that displays the full analysis of a swimming race,
 * consuming the GET /sessions/:id payload directly.
 *
 * 8 sections, ordered for coaches:
 *   1. Race Header (athlete, event, style, lane, status, quality)
 *   2. Race Summary (strokes, SR, DPS, hydro score — executive overview)
 *   3. 50m Splits table
 *   4. Main Chart with tabs (Pace / Strokes / Hydro)
 *   5. Video + Timeline with clickable events
 *   6. Hydrodynamic Efficiency (cards + interpretation)
 *   7. Technical Details (signal, confidence, finish_reason, window_unstable)
 *   8. Auto-generated Observations
 *
 * Route: /swim-vision/result/:sessionId
 *
 * @author Arena Stats / Paulo Brant
 * @date   2026-04-04
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import {
  getBiomechanicsSession,
  checkSessionStatus,
  getRaceContext,
} from '../services/biomechanicsService';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea, ComposedChart,
} from 'recharts';
import {
  Activity, Timer, Waves, Zap, Brain, Gauge, TrendingUp,
  Eye, Target, AlertTriangle, CheckCircle2,
  Ruler, Droplets, Wind, Clock, Flame, ArrowLeft,
  BarChart3, Shield, Cpu, RefreshCw, ChevronDown,
  Play, Pause, SkipBack, Maximize2, Info, Award,
  Anchor, Compass, Navigation, Layers, Fingerprint,
  Settings, FileText, Lightbulb, ThumbsUp,
} from 'lucide-react';
import { normalizeSession } from '../utils/normalizeSession';
import LapBlocks from '../components/swimming/LapBlocks';

// ═══════════════════════════════════════════════════════════════════
// Design Tokens
// ═══════════════════════════════════════════════════════════════════

const T = {
  primary: VUKZ.primary,
  bg: '#fafafa',
  card: '#ffffff',
  border: '#e5e7eb',
  text: '#111111',
  textLight: '#6b7280',
  textMuted: '#9ca3af',
  success: '#16a34a',
  successBg: '#f0fdf4',
  warning: '#f59e0b',
  warningBg: '#fffbeb',
  error: '#dc2626',
  errorBg: '#fef2f2',
  info: '#3b82f6',
  infoBg: '#eff6ff',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
  hydro: '#0ea5e9',
  hydroBg: '#f0f9ff',
  hydroDark: '#0369a1',
};

const STYLE_LABELS = {
  freestyle: 'Nado Livre', backstroke: 'Costas', butterfly: 'Borboleta',
  breaststroke: 'Peito', medley: 'Medley',
  crawl: 'Nado Livre',
};

const STYLE_EMOJI = {
  freestyle: '🏊', crawl: '🏊', backstroke: '🔄',
  butterfly: '🦋', breaststroke: '🐸', medley: '🏅',
};

// ═══════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════

const fmtTime = (s) => {
  if (s == null || isNaN(s)) return '—';
  const min = Math.floor(s / 60);
  const sec = s % 60;
  return min > 0 ? `${min}:${sec.toFixed(2).padStart(5, '0')}` : `${sec.toFixed(2)}s`;
};

const fmtScore = (v) => v != null ? v.toFixed(1) : '—';

const getQualityColor = (flag) => {
  if (flag === 'good') return T.success;
  if (flag === 'fair') return T.warning;
  if (flag === 'poor') return T.error;
  return T.textMuted;
};

const getQualityBg = (flag) => {
  if (flag === 'good') return T.successBg;
  if (flag === 'fair') return T.warningBg;
  if (flag === 'poor') return T.errorBg;
  return '#f3f4f6';
};

const getQualityLabel = (flag) => {
  if (flag === 'good') return 'Alta';
  if (flag === 'fair') return 'Média';
  if (flag === 'poor') return 'Baixa';
  return 'Desconhecida';
};

const getHydroClassification = (score) => {
  if (score == null) return { label: '—', color: T.textMuted };
  if (score >= 85) return { label: 'Elite', color: T.success };
  if (score >= 70) return { label: 'Bom', color: T.info };
  if (score >= 50) return { label: 'Regular', color: T.warning };
  return { label: 'Baixo', color: T.error };
};

// ═══════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════

/** Metric card used in summary and hydro sections */
const StatCard = ({ icon: Icon, label, value, unit, highlight, color, subtitle }) => (
  <div className={`rounded-xl border p-3 flex flex-col ${highlight ? 'ring-2 ring-offset-1' : ''}`}
    style={{
      borderColor: highlight ? (color || T.primary) : T.border,
      ringColor: highlight ? (color || T.primary) : undefined,
      backgroundColor: T.card,
    }}>
    <div className="flex items-center gap-1.5 mb-1">
      {Icon && <Icon size={12} style={{ color: color || T.textLight }} />}
      <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>{label}</span>
    </div>
    <div className="flex items-baseline gap-1">
      <span className="text-xl font-black" style={{ color: color || T.text }}>{value}</span>
      {unit && <span className="text-[10px] font-semibold" style={{ color: T.textLight }}>{unit}</span>}
    </div>
    {subtitle && <span className="text-[10px] mt-0.5" style={{ color: T.textLight }}>{subtitle}</span>}
  </div>
);

/** Badge pill */
const Badge = ({ children, color, bg }) => (
  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
    style={{ backgroundColor: bg || '#f3f4f6', color: color || T.textLight }}>
    {children}
  </span>
);

/** Section wrapper with title */
const Section = ({ id, title, icon: Icon, children, badge, className = '' }) => (
  <section id={id} className={`rounded-2xl border bg-white overflow-hidden ${className}`} style={{ borderColor: T.border }}>
    <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: T.border }}>
      {Icon && <Icon size={14} style={{ color: T.primary }} />}
      <h2 className="text-xs font-black uppercase tracking-wide" style={{ color: T.text }}>{title}</h2>
      {badge && <div className="ml-auto">{badge}</div>}
    </div>
    <div className="p-4">
      {children}
    </div>
  </section>
);

/** Loading skeleton */
const SectionSkeleton = ({ lines = 3 }) => (
  <div className="space-y-3 animate-pulse">
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="h-4 rounded-lg" style={{ backgroundColor: '#f3f4f6', width: `${80 - i * 15}%` }} />
    ))}
  </div>
);

/** Unavailable metric placeholder */
const Unavailable = ({ message = 'Indisponível nesta análise' }) => (
  <div className="flex items-center gap-2 py-3 px-4 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
    <Info size={14} style={{ color: T.textMuted }} />
    <span className="text-xs" style={{ color: T.textLight }}>{message}</span>
  </div>
);


// ═══════════════════════════════════════════════════════════════════
// Section 1: Race Header
// ═══════════════════════════════════════════════════════════════════

const RaceHeader = ({ session, raceCtx }) => {
  const style = session.swimming_style || session.swimmingStyle || '';
  const emoji = STYLE_EMOJI[style.toLowerCase()] || '🏊';
  const styleLabel = STYLE_LABELS[style.toLowerCase()] || style;
  const status = session.status;
  const lane = raceCtx?.lanes?.[0];
  const countQuality = session.countQuality;
  const strokeVersion = session.strokeVersion;

  // Derive analysis quality from multiple signals
  const overallQuality = useMemo(() => {
    if (countQuality === 'good' && session.overall_score >= 60) return 'good';
    if (countQuality === 'poor' || (session.overall_score != null && session.overall_score < 40)) return 'poor';
    return 'fair';
  }, [countQuality, session.overall_score]);

  return (
    <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: T.primary }}>
      {/* Black header bar */}
      <div className="px-5 py-4" style={{ background: VUKZ_GRADIENT }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{emoji}</span>
            <div>
              <h1 className="text-lg font-black text-white leading-tight">
                {lane?.athlete_name || raceCtx?.competition_name || `Sessão #${session.session_id}`}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-white/70 font-semibold">{styleLabel}</span>
                {(session.raceDistanceM || session.race_distance_m) && (
                  <span className="text-xs text-white/50">• {session.raceDistanceM || session.race_distance_m}m</span>
                )}
                {session.pool_length_m && (
                  <span className="text-xs text-white/50">• Piscina {session.pool_length_m}m</span>
                )}
                {lane?.lane_number && (
                  <span className="text-xs text-white/50">• Raia {lane.lane_number}</span>
                )}
              </div>
            </div>
          </div>

          {/* Status badge */}
          <div className="flex items-center gap-2">
            {status === 'completed' ? (
              <Badge color="#fff" bg="#16a34a">
                <CheckCircle2 size={10} /> Completa
              </Badge>
            ) : status === 'processing' ? (
              <Badge color="#fff" bg={T.warning}>
                <RefreshCw size={10} className="animate-spin" /> Processando
              </Badge>
            ) : status === 'failed' ? (
              <Badge color="#fff" bg={T.error}>
                <AlertTriangle size={10} /> Falhou
              </Badge>
            ) : (
              <Badge color="#fff" bg={T.textMuted}>
                {status}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Metadata row */}
      <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" style={{ backgroundColor: '#f9fafb' }}>
        {(raceCtx?.competition_name || session.competitionName) && (
          <div>
            <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>Competição</span>
            <p className="text-[11px] font-bold truncate" style={{ color: T.text }}>{raceCtx?.competition_name || session.competitionName}</p>
          </div>
        )}
        {(raceCtx?.event_name || session.eventName) && (
          <div>
            <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>Prova</span>
            <p className="text-[11px] font-bold truncate" style={{ color: T.text }}>{raceCtx?.event_name || session.eventName}</p>
          </div>
        )}
        {(raceCtx?.phase || session.officialFase) && (
          <div>
            <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>Fase</span>
            <p className="text-[11px] font-bold" style={{ color: T.text }}>{raceCtx?.phase || session.officialFase}</p>
          </div>
        )}
        <div>
          <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>Qualidade</span>
          <p className="text-[11px] font-bold" style={{ color: getQualityColor(overallQuality) }}>
            {getQualityLabel(overallQuality)}
          </p>
        </div>
        <div>
          <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>Contador</span>
          <p className="text-[11px] font-bold" style={{ color: T.text }}>{strokeVersion || '—'}</p>
        </div>
        <div>
          <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>Processamento</span>
          <p className="text-[11px] font-bold" style={{ color: T.text }}>
            {session.processing_time_ms ? `${(session.processing_time_ms / 1000).toFixed(1)}s` : '—'}
          </p>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Section 2: Race Summary (Executive Overview)
// ═══════════════════════════════════════════════════════════════════

const RaceSummary = ({ session }) => {
  // ── All values from normalized session (single source of truth) ──
  const displayTime = session.raceTimeDisplay || fmtTime(session.totalTimeOfficial || session.raceDurationSeconds);
  const startToFirst = session.startToFirstStrokeS;
  const hydro = session.hydroMetrics;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
      {/* Time */}
      <StatCard icon={Timer} label="Tempo" value={displayTime} highlight color={T.primary}
        subtitle={session.totalTimeOfficial ? 'Oficial' : session.raceDurationSeconds ? 'Estimado (IA)' : null} />

      {/* Stroke count — SINGLE canonical source */}
      <StatCard icon={Zap} label="Braçadas" value={session.canonicalStrokeCount} highlight color={T.info} />

      {/* Stroke rate */}
      <StatCard icon={Activity} label="Stroke Rate" value={session.canonicalStrokeRate ? session.canonicalStrokeRate.toFixed(1) : '—'} unit="br/min" color={T.purple} />

      {/* DPS */}
      <StatCard icon={Ruler} label="DPS" value={session.canonicalDPS || '—'} unit="m/br" color={T.cyan}
        subtitle="Distância por braçada" />

      {/* Start to first stroke */}
      <StatCard icon={Flame} label="Saída → 1ª Braçada" value={fmtTime(startToFirst)} color={T.warning} />

      {/* Hydro efficiency */}
      {hydro?.hydroEfficiencyScore != null ? (
        <StatCard icon={Waves} label="Hydro Score" value={fmtScore(hydro.hydroEfficiencyScore)} unit="/100"
          highlight color={T.hydro}
          subtitle={getHydroClassification(hydro.hydroEfficiencyScore).label} />
      ) : (
        <StatCard icon={Waves} label="Hydro Score" value="—" color={T.textMuted}
          subtitle="Aguardando persistência" />
      )}

      {/* Turn count */}
      {session.turnDetection && (
        <StatCard icon={Navigation} label="Viradas" value={session.turnDetection.turnCount ?? '—'}
          color={T.text}
          subtitle={session.turnDetection.avgQualityScore != null ? `Q: ${session.turnDetection.avgQualityScore.toFixed(0)}/100` : null} />
      )}

      {/* Lap blocks count */}
      {session.numLaps > 1 && (
        <StatCard icon={Layers} label="Parciais" value={`${session.numLaps}×${session.poolLengthM}m`}
          color={T.text} subtitle={session.pacingType === 'negative_split' ? '📈 Negativo' : session.pacingType === 'positive_split' ? '📉 Positivo' : null} />
      )}

      {/* Overall score */}
      {session.overall_score != null && (
        <StatCard icon={Award} label="Score Geral" value={session.overall_score.toFixed(1)} unit="/100"
          color={session.overall_score >= 70 ? T.success : session.overall_score >= 50 ? T.warning : T.error} />
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Section 3: 50m Splits Table
// ═══════════════════════════════════════════════════════════════════

const SplitsSection = ({ session }) => {
  const splitsOfficial = session.splitsOfficial || [];
  const splitAnalysis = session.splitAnalysis;
  const raceDist = session.race_distance_m || session.raceDistanceM || 50;
  const strokeTiming = session.strokeTiming;
  const strokingSummary = strokeTiming?.summary || {};

  // For 50m races — show phase breakdown
  const is50m = raceDist <= 50;

  if (splitsOfficial.length === 0 && !splitAnalysis && !is50m) {
    return <Unavailable message="Parciais não disponíveis — vincule a prova a uma competição para dados oficiais" />;
  }

  // Official splits table
  if (splitsOfficial.length > 0) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b" style={{ borderColor: T.border }}>
              <th className="text-left py-2 px-3 font-bold uppercase tracking-wider text-[9px]" style={{ color: T.textLight }}>Parcial</th>
              <th className="text-right py-2 px-3 font-bold uppercase tracking-wider text-[9px]" style={{ color: T.textLight }}>Tempo</th>
              <th className="text-right py-2 px-3 font-bold uppercase tracking-wider text-[9px]" style={{ color: T.textLight }}>Observação</th>
            </tr>
          </thead>
          <tbody>
            {splitsOfficial.map((sp, i) => (
              <tr key={i} className="border-b last:border-0" style={{ borderColor: T.border }}>
                <td className="py-2.5 px-3 font-bold" style={{ color: T.text }}>{sp.distance}</td>
                <td className="py-2.5 px-3 text-right font-black" style={{ color: T.primary }}>{fmtTime(sp.timeS)}</td>
                <td className="py-2.5 px-3 text-right" style={{ color: T.textLight }}>
                  {i === 0 ? 'saída + aceleração' : i === splitsOfficial.length - 1 ? 'fechamento' : 'manutenção'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Fallback: 50m phase breakdown
  if (is50m) {
    const phases = [
      { label: '0–15m', desc: 'Saída + Breakout', time: strokingSummary.breakoutTimeS },
      { label: '15–25m', desc: 'Aceleração', time: null },
      { label: '25–35m', desc: 'Nado Limpo', time: null },
      { label: '35–50m', desc: 'Chegada', time: null },
    ];
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b" style={{ borderColor: T.border }}>
              <th className="text-left py-2 px-3 font-bold uppercase tracking-wider text-[9px]" style={{ color: T.textLight }}>Trecho</th>
              <th className="text-left py-2 px-3 font-bold uppercase tracking-wider text-[9px]" style={{ color: T.textLight }}>Fase</th>
              <th className="text-right py-2 px-3 font-bold uppercase tracking-wider text-[9px]" style={{ color: T.textLight }}>Tempo</th>
            </tr>
          </thead>
          <tbody>
            {phases.map((p, i) => (
              <tr key={i} className="border-b last:border-0" style={{ borderColor: T.border }}>
                <td className="py-2.5 px-3 font-bold" style={{ color: T.text }}>{p.label}</td>
                <td className="py-2.5 px-3" style={{ color: T.textLight }}>{p.desc}</td>
                <td className="py-2.5 px-3 text-right font-black" style={{ color: T.primary }}>{fmtTime(p.time)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[10px] mt-2 italic" style={{ color: T.textMuted }}>
          Parciais por trecho disponíveis com vinculação à competição ou em distâncias ≥100m.
        </p>
      </div>
    );
  }

  return <Unavailable message="Dados de parciais não disponíveis para esta análise" />;
};


// ═══════════════════════════════════════════════════════════════════
// Section 4a: Strokes Interval Chart (Enhanced v2)
// ═══════════════════════════════════════════════════════════════════

const StrokesIntervalChart = ({ intervalData, medianInterval, officialLines }) => {
  const [focusMode, setFocusMode] = useState(false);

  const suspectThresh = medianInterval * 1.8;
  const missedThresh  = medianInterval * 2.5;
  const overcountThresh = medianInterval * 0.4;
  const focusCeil = medianInterval * 2.8;
  const yDomain = focusMode ? [0, focusCeil] : [0, 'auto'];

  const missedCount = intervalData.filter(d => d.status === 'missed').length;

  const renderDot = ({ cx, cy, payload, index }) => {
    if (cx == null || cy == null) return null;
    const st = payload.status;
    const clr = st === 'missed' ? T.error
      : st === 'suspect' ? T.warning
      : st === 'overcount' ? T.purple
      : st === 'first' ? T.cyan
      : T.success;
    const r = st === 'first' ? 5 : st === 'ok' ? 2.5 : 4;
    const isClamped = focusMode && payload.interval > focusCeil;
    const finalCy = isClamped ? 16 : cy;

    return (
      <g key={`dot-${index}`}>
        {st === 'missed' && (
          <circle cx={cx} cy={finalCy} r={4} fill="none" stroke={T.error} strokeWidth={1.5} opacity={0.6}>
            <animate attributeName="r" from="4" to="13" dur="1.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" from="0.7" to="0" dur="1.5s" repeatCount="indefinite" />
          </circle>
        )}
        <circle cx={cx} cy={finalCy} r={r} fill={clr} stroke="#fff" strokeWidth={1} />
        {st === 'missed' && (
          <text x={cx} y={finalCy - 10} textAnchor="middle" fontSize={7} fontWeight={800} fill={T.error}>
            GAP
          </text>
        )}
        {isClamped && (
          <text x={cx} y={finalCy - 1} textAnchor="middle" fontSize={10}>⚠️</text>
        )}
      </g>
    );
  };

  // Custom tooltip
  const IntervalTip = ({ active, payload: pl }) => {
    if (!active || !pl?.length) return null;
    const point = pl[0]?.payload;
    const st = point?.status;
    return (
      <div className="rounded-xl border shadow-xl px-3 py-2 text-xs min-w-[150px]"
        style={{
          borderColor: st === 'missed' ? T.error : st === 'suspect' ? T.warning : T.border,
          borderWidth: st === 'missed' || st === 'suspect' ? 2 : 1,
          backgroundColor: st === 'missed' ? T.errorBg : st === 'suspect' ? T.warningBg : '#fff',
        }}>
        <p className="font-bold mb-0.5" style={{ color: T.text }}>
          Braçada #{point?.number || '?'} · {fmtTime(point?.timeS)}
        </p>
        {st === 'missed' && (
          <p className="font-black text-[10px] mb-1" style={{ color: T.error }}>
            ⚠ Braçada Perdida — gap de {fmtTime(point?.interval)}
          </p>
        )}
        {st === 'suspect' && (
          <p className="font-bold text-[10px] mb-1" style={{ color: T.warning }}>
            ⚡ Intervalo suspeito
          </p>
        )}
        {st === 'overcount' && (
          <p className="font-bold text-[10px] mb-1" style={{ color: T.purple }}>
            ✦ Possível braçada extra
          </p>
        )}
        <p style={{ color: T.warning }}>
          Intervalo: <span className="font-bold">{fmtTime(point?.interval)}</span>s
        </p>
      </div>
    );
  };

  return (
    <div>
      {/* Focus toggle + gap count */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {missedCount > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
              style={{ backgroundColor: T.errorBg, color: T.error }}>
              {missedCount} gap{missedCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={() => setFocusMode(!focusMode)}
          className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold transition-all"
          style={{
            backgroundColor: focusMode ? T.primary : '#f3f4f6',
            color: focusMode ? '#fff' : T.textLight,
          }}>
          <Target size={10} />
          {focusMode ? 'Foco ON' : 'Foco'}
        </button>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={intervalData} margin={{ top: 18, right: 15, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.border} />

          {/* Zone shading */}
          <ReferenceArea y1={overcountThresh} y2={medianInterval} fill={T.success} fillOpacity={0.06} ifOverflow="hidden" />
          <ReferenceArea y1={medianInterval} y2={suspectThresh} fill={T.warning} fillOpacity={0.04} ifOverflow="hidden" />
          <ReferenceArea y1={suspectThresh} y2={missedThresh} fill={T.warning} fillOpacity={0.08} ifOverflow="hidden" />
          <ReferenceArea y1={missedThresh} y2={focusMode ? focusCeil : 999} fill={T.error} fillOpacity={0.08} ifOverflow="hidden" />

          <XAxis dataKey="timeS" type="number" domain={['dataMin', 'dataMax']}
            tickFormatter={fmtTime} tick={{ fontSize: 9, fill: T.textLight }} />
          <YAxis domain={yDomain} tick={{ fontSize: 9, fill: T.textLight }}
            label={{ value: 'Intervalo (s)', angle: -90, position: 'insideLeft', fontSize: 9, fill: T.textLight }}
            allowDataOverflow={focusMode} />
          <Tooltip content={<IntervalTip />} />

          <ReferenceLine y={medianInterval} stroke={T.primary} strokeDasharray="6 3" strokeWidth={1.5}
            label={{ value: `Med ${fmtTime(medianInterval)}`, position: 'right', fontSize: 8, fill: T.primary, fontWeight: 700 }} />
          <ReferenceLine y={suspectThresh} stroke={T.warning} strokeDasharray="3 3" strokeWidth={1}
            label={{ value: 'Suspeito', position: 'right', fontSize: 7, fill: T.warning }} />
          <ReferenceLine y={missedThresh} stroke={T.error} strokeDasharray="3 3" strokeWidth={1}
            label={{ value: 'Perdida', position: 'right', fontSize: 7, fill: T.error }} />
          <ReferenceLine y={overcountThresh} stroke={T.purple} strokeDasharray="2 4" strokeWidth={0.8} />

          {officialLines.map((ol, i) => (
            <ReferenceLine key={i} x={ol.x} stroke={ol.color} strokeDasharray={ol.dash} strokeWidth={1} />
          ))}
          <Line type="monotone" dataKey="interval" stroke={T.warning} strokeWidth={2}
            dot={renderDot}
            activeDot={{ r: 6, stroke: T.primary, strokeWidth: 2, fill: '#fff' }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 mt-1 text-[9px]" style={{ color: T.textLight }}>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: T.cyan }} /> Saída→1ª</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: T.success }} /> OK</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: T.warning }} /> Suspeito</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: T.error }} /> Perdida</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: T.purple }} /> Extra</span>
        <span className="mx-0.5 opacity-30">|</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-1.5 rounded-sm inline-block" style={{ backgroundColor: T.success, opacity: 0.2 }} /> Zona ideal</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-1.5 rounded-sm inline-block" style={{ backgroundColor: T.error, opacity: 0.2 }} /> Zona gap</span>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Section 4: Main Chart with Tabs
// ═══════════════════════════════════════════════════════════════════

const CHART_TABS = [
  { key: 'strokes', label: 'Intervalo entre Braçadas', icon: Zap },
  { key: 'pace', label: 'Tempo Acumulado', icon: TrendingUp },
  { key: 'hydro', label: 'Hidrodinâmica', icon: Waves },
];

const MainChart = ({ session }) => {
  const [tab, setTab] = useState('strokes');
  const strokeTiming = session.strokeTiming;
  const strokes = strokeTiming?.strokes || [];
  const summary = strokeTiming?.summary || {};
  const splitsOfficial = session.splitsOfficial || [];
  const reaction = session.reactionTimeOfficial;
  const totalOfficial = session.totalTimeOfficial;
  const hasStrokeData = strokes.length > 0;

  // Race time origin
  const raceOrigin = useMemo(() => {
    if (summary.expectedStartS > 0) return summary.expectedStartS;
    if (strokes.length > 0) return Math.max(0, strokes[0].videoTimeS - 2);
    return 0;
  }, [summary, strokes]);

  // Cumulative stroke data
  const cumulativeData = useMemo(() => {
    if (!strokes.length) return [];
    const cum = [{ timeS: 0, strokes: 0 }];
    strokes.forEach(s => cum.push({ timeS: +(s.videoTimeS - raceOrigin).toFixed(3), strokes: s.number }));
    return cum;
  }, [strokes, raceOrigin]);

  // Interval data
  const medianInterval = summary.medianIntervalS || summary.avgIntervalS || 1;
  const intervalData = useMemo(() => {
    if (!strokes.length) return [];
    const startToFirst = session.startToFirstStrokeS;
    return strokes.map((s, i) => {
      const t = +(s.videoTimeS - raceOrigin).toFixed(3);
      let iv;
      if (i === 0) {
        iv = startToFirst != null ? startToFirst : (s.videoTimeS - raceOrigin);
      } else {
        iv = s.intervalS;
      }
      const status = i === 0 ? 'first'
        : iv == null ? 'ok'
        : iv > medianInterval * 2.5 ? 'missed'
        : iv > medianInterval * 1.8 ? 'suspect'
        : iv < medianInterval * 0.4 ? 'overcount'
        : 'ok';
      return { timeS: t, interval: iv != null ? +iv.toFixed(3) : null, status, number: s.number };
    }).filter(d => d.interval != null);
  }, [strokes, raceOrigin, session.startToFirstStrokeS, medianInterval]);

  // Official reference lines
  const officialLines = useMemo(() => {
    const lines = [];
    if (reaction != null) lines.push({ x: reaction, label: `Reação ${fmtTime(reaction)}`, color: T.cyan, dash: '6 3' });
    splitsOfficial.forEach(sp => {
      if (sp.timeS != null) lines.push({ x: sp.timeS, label: sp.distance, color: T.info, dash: '4 4' });
    });
    if (totalOfficial != null) lines.push({ x: totalOfficial, label: `Final ${fmtTime(totalOfficial)}`, color: T.error, dash: '0' });
    return lines;
  }, [reaction, splitsOfficial, totalOfficial]);

  if (!hasStrokeData) {
    return <Unavailable message="Dados de braçada não disponíveis — processe um vídeo para visualizar os gráficos" />;
  }

  return (
    <div className="space-y-3">
      {/* Tab selector */}
      <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: '#f3f4f6' }}>
        {CHART_TABS.map(ct => (
          <button key={ct.key} onClick={() => setTab(ct.key)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wide transition-all"
            style={{
              backgroundColor: tab === ct.key ? T.card : 'transparent',
              color: tab === ct.key ? T.text : T.textLight,
              boxShadow: tab === ct.key ? '0 1px 3px rgba(0,0,0,0.08)' : undefined,
            }}>
            <ct.icon size={11} />
            {ct.label}
          </button>
        ))}
      </div>

      {/* Tab: Pace */}
      {tab === 'pace' && (
        <div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={cumulativeData} margin={{ top: 5, right: 15, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="strokes" type="number" domain={['dataMin', 'dataMax']}
                tick={{ fontSize: 9, fill: T.textLight }}
                label={{ value: 'Braçada', position: 'insideBottomRight', offset: -5, fontSize: 9, fill: T.textLight }} />
              <YAxis dataKey="timeS" type="number" domain={['dataMin', 'dataMax']}
                tickFormatter={fmtTime} tick={{ fontSize: 9, fill: T.textLight }}
                label={{ value: 'Tempo (s)', angle: -90, position: 'insideLeft', fontSize: 9, fill: T.textLight }} />
              <Tooltip formatter={(v, name) => [name === 'timeS' ? fmtTime(v) : v, 'Tempo']} labelFormatter={(v) => `Braçada ${v}`}
                contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              {officialLines.map((ol, i) => (
                <ReferenceLine key={i} y={ol.x} stroke={ol.color} strokeDasharray={ol.dash} strokeWidth={1.5}
                  label={{ value: ol.label, position: 'right', fontSize: 8, fill: ol.color, fontWeight: 700 }} />
              ))}
              <Line type="stepAfter" dataKey="timeS" stroke={T.primary} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-[9px]" style={{ color: T.textLight }}>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: T.primary }} /> Tempo acumulado</span>
            {reaction != null && <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: T.cyan }} /> Reação</span>}
            {totalOfficial != null && <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: T.error }} /> Final</span>}
          </div>
        </div>
      )}

      {/* Tab: Strokes */}
      {tab === 'strokes' && (
        <StrokesIntervalChart
          intervalData={intervalData}
          medianInterval={medianInterval}
          officialLines={officialLines}
        />
      )}

      {/* Tab: Hydro */}
      {tab === 'hydro' && (
        <div>
          {session.hydroMetrics ? (
            <div className="space-y-3">
              {/* Hydro bar chart — simplified since we don't have per-frame data in the frontend yet */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Alinhamento', value: session.hydroMetrics.bodyAlignmentScore, icon: Compass },
                  { label: 'Oscilação', value: session.hydroMetrics.oscillationScore, icon: Activity },
                  { label: 'Estabilidade', value: session.hydroMetrics.bodyStabilityScore, icon: Shield },
                ].map((m, i) => (
                  <div key={i} className="rounded-lg border p-3" style={{ borderColor: T.border }}>
                    <div className="flex items-center gap-1.5 mb-2">
                      <m.icon size={11} style={{ color: T.hydro }} />
                      <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>{m.label}</span>
                    </div>
                    <div className="text-2xl font-black" style={{ color: T.text }}>{m.value != null ? m.value.toFixed(1) : '—'}</div>
                    {/* Score bar */}
                    <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#f3f4f6' }}>
                      <div className="h-full rounded-full transition-all" style={{
                        width: `${Math.min(100, m.value || 0)}%`,
                        backgroundColor: (m.value || 0) >= 85 ? T.success : (m.value || 0) >= 70 ? T.hydro : (m.value || 0) >= 50 ? T.warning : T.error,
                      }} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] italic" style={{ color: T.textMuted }}>
                Dados per-frame de hidrodinâmica disponíveis em versões futuras para gráfico temporal completo.
              </p>
            </div>
          ) : (
            <Unavailable message="Métricas hidrodinâmicas não disponíveis — aguardando migration 188 ou re-processamento" />
          )}
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Section 5: Video + Timeline
// ═══════════════════════════════════════════════════════════════════

const VideoTimeline = ({ session }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoError, setVideoError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  // Primary: annotated, Fallback: original blob
  const annotatedUrl = session.annotatedVideoUrl;
  const originalUrl = session.videoBlobUrl || session.video_blob_url;
  const videoUrl = usingFallback ? originalUrl : (annotatedUrl || originalUrl);
  const strokeTiming = session.strokeTiming;
  const strokes = strokeTiming?.strokes || [];
  const turns = session.turnDetection?.turns || [];
  const startDetection = session.startDetection;

  // Build timeline events
  const events = useMemo(() => {
    const evts = [];
    // Start
    if (startDetection?.startFrameS != null) {
      evts.push({ time: startDetection.startFrameS, type: 'start', label: 'Largada' });
    }
    // Strokes
    strokes.forEach(s => {
      if (s.videoTimeS != null) {
        evts.push({ time: s.videoTimeS, type: 'stroke', label: `Braçada #${s.number}` });
      }
    });
    // Turns
    turns.forEach(t => {
      if (t.videoTimeS != null) {
        evts.push({ time: t.videoTimeS, type: 'turn', label: `Virada #${t.turnNumber}` });
      }
    });
    return evts.sort((a, b) => a.time - b.time);
  }, [startDetection, strokes, turns]);

  const seekTo = useCallback((time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      videoRef.current.play();
      setPlaying(true);
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); } else { videoRef.current.play(); }
    setPlaying(!playing);
  }, [playing]);

  const hasVideo = !!videoUrl;

  const eventTypeColors = {
    start: T.success,
    stroke: T.info,
    turn: T.warning,
    finish: T.error,
  };

  return (
    <div className="space-y-3">
      {/* Video player — only render if URL available */}
      {hasVideo ? (
      <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
        {videoError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90">
            <div className="text-center p-4">
              <p className="text-red-400 text-sm font-bold mb-1">❌ Erro ao carregar vídeo</p>
              <p className="text-gray-400 text-[10px]">{videoError}</p>
              <button onClick={() => { setVideoError(null); setUsingFallback(false); if (videoRef.current) videoRef.current.load(); }}
                className="mt-2 px-3 py-1 rounded text-[10px] font-bold bg-white/10 text-white hover:bg-white/20">
                Tentar novamente
              </button>
            </div>
          </div>
        ) : usingFallback ? (
          <div className="absolute top-2 left-2 z-10 bg-yellow-500/90 text-black text-[10px] px-2 py-0.5 rounded font-bold">
            ⚠️ Vídeo original (overlay indisponível)
          </div>
        ) : null}
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
          onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onError={(e) => {
            const msg = e.target.error?.message || 'Formato não suportado ou URL expirada';
            console.error('[Video] Load error:', msg, 'src:', videoUrl?.substring(0, 100));
            if (!usingFallback && originalUrl && videoUrl !== originalUrl) {
              console.warn('[Video] Annotated failed, falling back to original');
              setUsingFallback(true);
              setVideoError(null);
            } else {
              setVideoError(msg);
            }
          }}
          playsInline
        />
        <button onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-all">
          {!playing && <Play size={48} className="text-white/80" fill="white" />}
        </button>
      </div>
      ) : (
        <div className="rounded-xl bg-gray-100 p-4 text-center text-sm text-gray-400">
          📹 Vídeo não disponível — dados de análise exibidos abaixo
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center gap-2">
        <button onClick={() => seekTo(0)} className="p-1.5 rounded-lg hover:bg-gray-100">
          <SkipBack size={14} style={{ color: T.text }} />
        </button>
        <button onClick={togglePlay} className="p-1.5 rounded-lg hover:bg-gray-100">
          {playing ? <Pause size={14} style={{ color: T.text }} /> : <Play size={14} style={{ color: T.text }} />}
        </button>
        <span className="text-[10px] font-bold" style={{ color: T.textLight }}>
          {fmtTime(currentTime)} / {fmtTime(duration)}
        </span>
        {/* Timeline bar */}
        <div className="flex-1 h-6 relative cursor-pointer rounded-lg overflow-hidden" style={{ backgroundColor: '#f3f4f6' }}
          onClick={(e) => {
            if (!duration) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const pct = (e.clientX - rect.left) / rect.width;
            seekTo(pct * duration);
          }}>
          {/* Progress */}
          <div className="absolute top-0 left-0 h-full" style={{
            width: `${duration ? (currentTime / duration) * 100 : 0}%`,
            backgroundColor: T.primary + '30',
          }} />
          {/* Event markers */}
          {events.map((evt, i) => duration > 0 && (
            <div key={i} className="absolute top-0 h-full w-0.5 cursor-pointer group"
              style={{ left: `${(evt.time / duration) * 100}%`, backgroundColor: eventTypeColors[evt.type] || T.textMuted }}
              onClick={(e) => { e.stopPropagation(); seekTo(evt.time); }}
              title={evt.label}>
              {/* Tooltip on hover */}
              <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-0.5 rounded text-[9px] font-bold whitespace-nowrap"
                style={{ backgroundColor: T.text, color: '#fff' }}>
                {evt.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Event list */}
      {events.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {events.filter(e => e.type !== 'stroke' || (e.label.includes('#1') || e.label.includes('#5') || e.label.includes('#10') || e.label.includes('#15') || e.label.includes('#20') || e.label.includes('#25') || e.label.includes('#29') || e.label.includes('#30'))).map((evt, i) => (
            <button key={i} onClick={() => seekTo(evt.time)}
              className="text-[9px] font-bold px-2 py-1 rounded-md border hover:shadow-sm transition-all"
              style={{ borderColor: eventTypeColors[evt.type] + '40', color: eventTypeColors[evt.type] }}>
              {evt.label}
            </button>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-3 text-[9px]" style={{ color: T.textLight }}>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: T.success }} /> Largada</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: T.info }} /> Braçada</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: T.warning }} /> Virada</span>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Section 6: Hydrodynamic Efficiency
// ═══════════════════════════════════════════════════════════════════

const HydroSection = ({ session }) => {
  const hydro = session.hydroMetrics;

  if (!hydro) {
    return (
      <div className="text-center py-6">
        <Waves size={32} className="mx-auto mb-2 opacity-30" style={{ color: T.hydro }} />
        <p className="text-sm font-bold" style={{ color: T.text }}>Métricas Hidrodinâmicas</p>
        <p className="text-xs mt-1" style={{ color: T.textLight }}>
          Indisponíveis nesta análise. Reprocesse a sessão ou aguarde a migration 188.
        </p>
      </div>
    );
  }

  const cls = getHydroClassification(hydro.hydroEfficiencyScore);
  const style = (session.swimming_style || session.swimmingStyle || '').toLowerCase();

  // Generate interpretive text based on metrics
  const interpretations = [];

  if (hydro.bodyAlignmentScore != null) {
    if (hydro.bodyAlignmentScore >= 85) {
      interpretations.push({ type: 'success', text: 'Corpo bem alinhado durante a maior parte da prova — excelente streamline.' });
    } else if (hydro.bodyAlignmentScore >= 70) {
      interpretations.push({ type: 'info', text: 'Alinhamento corporal bom, com margem para melhoria na posição da cabeça.' });
    } else {
      interpretations.push({ type: 'warning', text: `Alinhamento corporal abaixo do ideal (${hydro.bodyAlignmentMeanDeg?.toFixed(1)}° médio). Foco: posição neutra da cabeça.` });
    }
  }

  if (hydro.oscillationScore != null) {
    const isButterfly = style === 'butterfly' || style === 'borboleta';
    if (isButterfly) {
      interpretations.push({ type: 'info', text: 'Oscilação vertical em borboleta — ondulação é parte da técnica. Score contextualizado.' });
    } else if (hydro.oscillationScore >= 85) {
      interpretations.push({ type: 'success', text: 'Oscilação vertical mínima — deslocamento eficiente e horizontal.' });
    } else if (hydro.oscillationScore >= 70) {
      interpretations.push({ type: 'info', text: 'Oscilação vertical dentro do esperado para o estilo.' });
    } else {
      interpretations.push({ type: 'warning', text: 'Oscilação vertical acima do ideal — corpo subindo e descendo excessivamente.' });
    }
  }

  if (hydro.bodyStabilityScore != null) {
    if (hydro.stabilityDegradation != null && hydro.stabilityDegradation > 10) {
      interpretations.push({ type: 'warning', text: `Estabilidade corporal caiu ${hydro.stabilityDegradation.toFixed(0)}% na parte final da prova — indicativo de fadiga de core.` });
    } else if (hydro.bodyStabilityScore >= 80) {
      interpretations.push({ type: 'success', text: 'Core estável ao longo de toda a prova — manutenção consistente da técnica.' });
    }
  }

  return (
    <div className="space-y-4">
      {/* Main hydro score */}
      <div className="flex items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: T.hydroBg, border: `1px solid ${T.hydro}30` }}>
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{
          background: `conic-gradient(${cls.color} ${(hydro.hydroEfficiencyScore || 0) * 3.6}deg, #e5e7eb 0deg)`,
        }}>
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
            <span className="text-lg font-black" style={{ color: cls.color }}>{fmtScore(hydro.hydroEfficiencyScore)}</span>
          </div>
        </div>
        <div>
          <p className="text-sm font-black" style={{ color: T.text }}>Índice Hidrodinâmico</p>
          <p className="text-xs font-bold" style={{ color: cls.color }}>{cls.label}</p>
          <p className="text-[10px] mt-0.5" style={{ color: T.textLight }}>
            Confiança: {hydro.hydroConfidence != null ? `${(hydro.hydroConfidence * 100).toFixed(0)}%` : '—'}
            {hydro.hydroVersion && ` • Versão ${hydro.hydroVersion}`}
          </p>
        </div>
      </div>

      {/* Sub-metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard icon={Compass} label="Alinhamento Corporal" value={fmtScore(hydro.bodyAlignmentScore)} unit="/100"
          color={T.hydro} subtitle={hydro.bodyAlignmentMeanDeg != null ? `${hydro.bodyAlignmentMeanDeg.toFixed(1)}° médio` : null} />
        <StatCard icon={Activity} label="Oscilação Vertical" value={fmtScore(hydro.oscillationScore)} unit="/100"
          color={T.hydro} subtitle={hydro.verticalOscillation != null ? `σ=${hydro.verticalOscillation.toFixed(3)}` : null} />
        <StatCard icon={Shield} label="Estabilidade Corporal" value={fmtScore(hydro.bodyStabilityScore)} unit="/100"
          color={T.hydro} subtitle={hydro.perturbationCount != null ? `${hydro.perturbationCount} perturbações` : null} />
      </div>

      {/* Interpretive text */}
      {interpretations.length > 0 && (
        <div className="space-y-2">
          {interpretations.map((int, i) => (
            <div key={i} className="flex items-start gap-2 py-2 px-3 rounded-lg" style={{
              backgroundColor: int.type === 'success' ? T.successBg : int.type === 'warning' ? T.warningBg : T.infoBg,
            }}>
              {int.type === 'success' ? <CheckCircle2 size={13} style={{ color: T.success }} className="flex-shrink-0 mt-0.5" />
                : int.type === 'warning' ? <AlertTriangle size={13} style={{ color: T.warning }} className="flex-shrink-0 mt-0.5" />
                : <Info size={13} style={{ color: T.info }} className="flex-shrink-0 mt-0.5" />}
              <span className="text-xs leading-relaxed" style={{ color: T.text }}>{int.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Technical details */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
        <div className="p-2 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
          <span style={{ color: T.textLight }}>Frames analisados</span>
          <p className="font-bold" style={{ color: T.text }}>{hydro.framesAnalysed ?? '—'}</p>
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
          <span style={{ color: T.textLight }}>Frames válidos</span>
          <p className="font-bold" style={{ color: T.text }}>{hydro.framesValid ?? '—'}</p>
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
          <span style={{ color: T.textLight }}>Câmera estabilizada</span>
          <p className="font-bold" style={{ color: T.text }}>{hydro.cameraStabilized ? '✓ Sim' : '✗ Não'}</p>
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
          <span style={{ color: T.textLight }}>Analyzer</span>
          <p className="font-bold" style={{ color: T.text }}>{hydro.analyzerVersion ?? '—'}</p>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Section 7: Technical Details
// ═══════════════════════════════════════════════════════════════════

const TechnicalDetails = ({ session }) => {
  const strokeTiming = session.strokeTiming;
  const summary = strokeTiming?.summary || {};
  const countQuality = session.countQuality;
  const strokeVersion = session.strokeVersion;

  // Extract from dedicated API fields first, then fallback to summary/diagnostics
  const signalUsed = session.signalUsed || session.signal_used || summary.signalUsed || summary.signal_used || session._diagnostics?.signal_used || '—';
  const finishReason = session.finish_reason || session.finishReason || summary.finishReason || '—';
  const windowUnstable = session.window_unstable ?? session.windowUnstable ?? summary.windowUnstable ?? null;
  const confidence = session.countingConfidence ?? session.counting_confidence ?? summary.confidence ?? session._diagnostics?.confidence ?? null;
  const styleDetected = summary.styleDetected || session.swimming_style || '—';
  const fallback = summary.fallbackUsed || session._diagnostics?.fallback_used || false;
  const model = session.model_version || '—';
  const device = session.device_used || '—';
  const backbone = session.backbone || '—';

  const rows = [
    { label: 'Sinal Selecionado', value: signalUsed, icon: Activity },
    { label: 'Confidence', value: confidence != null ? `${(confidence * 100).toFixed(0)}%` : '—', icon: Target },
    { label: 'Count Quality', value: countQuality || '—', icon: Shield,
      color: getQualityColor(countQuality), bg: getQualityBg(countQuality) },
    { label: 'Contador', value: strokeVersion || '—', icon: Cpu },
    { label: 'Estilo Detectado', value: STYLE_LABELS[styleDetected?.toLowerCase()] || styleDetected, icon: Waves },
    { label: 'Fallback Usado', value: fallback ? 'Sim ⚠️' : 'Não ✓', icon: AlertTriangle,
      color: fallback ? T.warning : T.success },
    { label: 'Window Instável', value: windowUnstable === true ? 'Sim ⚠️' : windowUnstable === false ? 'Não ✓' : '—', icon: Eye,
      color: windowUnstable === true ? T.warning : windowUnstable === false ? T.success : T.textLight },
    { label: 'Finish Reason', value: finishReason, icon: Fingerprint },
    { label: 'Modelo', value: model, icon: Brain },
    { label: 'Device', value: device, icon: Cpu },
    { label: 'Backbone', value: backbone, icon: Layers },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
      {rows.map((r, i) => (
        <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg" style={{ backgroundColor: r.bg || '#f9fafb' }}>
          <r.icon size={12} style={{ color: r.color || T.textLight }} className="flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-[8px] font-bold uppercase tracking-wider block" style={{ color: T.textLight }}>{r.label}</span>
            <span className="text-[11px] font-bold" style={{ color: r.color || T.text }}>{r.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Section 7b: Gap Analysis Report (v3.12)
// ═══════════════════════════════════════════════════════════════════

const SeverityDot = ({ severity }) => {
  const colors = {
    critical: T.error,
    warning: T.warning,
  };
  return (
    <span className="w-2 h-2 rounded-full inline-block flex-shrink-0"
      style={{ backgroundColor: colors[severity] || T.textMuted }} />
  );
};

const RecoveryBadge = ({ result }) => {
  if (!result || !result.attempted) return <span className="text-[9px]" style={{ color: T.textMuted }}>—</span>;
  if (result.recovered > 0) {
    const method = result.method === 'interpolation' ? '📐' : '📈';
    return (
      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
        style={{ backgroundColor: T.successBg, color: T.success }}>
        {method} +{result.recovered}
      </span>
    );
  }
  return (
    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
      style={{ backgroundColor: T.errorBg, color: T.error }}>
      ✗ {result.reason_if_skipped?.replace(/_/g, ' ') || 'failed'}
    </span>
  );
};

const GapReport = ({ session }) => {
  const ga = session.gapAnalysis;
  if (!ga || !ga.gaps || ga.gaps.length === 0) {
    return (
      <div className="px-5 py-4 text-center">
        <CheckCircle2 size={20} className="mx-auto mb-1" style={{ color: T.success }} />
        <p className="text-[11px] font-bold" style={{ color: T.success }}>
          Sem gaps detectados — cobertura completa
        </p>
      </div>
    );
  }

  const summary = ga.gap_summary || {
    total: ga.total_gaps || 0,
    critical: ga.critical_gaps || 0,
    recovered: ga.recovered_strokes || 0,
    unresolved: 0,
    estimated_missing_total: 0,
  };

  const splitQuality = ga.split_quality || [];
  const gaps = ga.gaps || [];

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-5 gap-3" style={{ backgroundColor: '#f9fafb' }}>
        <div className="text-center">
          <div className="text-lg font-extrabold" style={{ color: summary.critical > 0 ? T.error : T.success }}>
            {summary.total}
          </div>
          <div className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>
            Gaps Total
          </div>
        </div>
        <div className="text-center">
          <div className="text-lg font-extrabold" style={{ color: T.error }}>
            {summary.critical}
          </div>
          <div className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>
            Críticos
          </div>
        </div>
        <div className="text-center">
          <div className="text-lg font-extrabold" style={{ color: T.success }}>
            {summary.recovered || 0}
          </div>
          <div className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>
            Recuperados
          </div>
        </div>
        <div className="text-center">
          <div className="text-lg font-extrabold" style={{ color: T.warning }}>
            {summary.estimated_missing_total || 0}
          </div>
          <div className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>
            Est. Perdidos
          </div>
        </div>
        <div className="text-center">
          <div className="text-lg font-extrabold" style={{ color: summary.unresolved > 0 ? T.error : T.success }}>
            {summary.unresolved || 0}
          </div>
          <div className="text-[8px] font-bold uppercase tracking-wider" style={{ color: T.textLight }}>
            Não Resolvidos
          </div>
        </div>
      </div>

      {/* Per-split quality (if multi-lap) */}
      {splitQuality.length > 1 && (
        <div className="px-5">
          <h4 className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: T.textLight }}>
            Qualidade por Parcial
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {splitQuality.map((sq) => {
              const pct = sq.quality_pct || 0;
              const barColor = pct >= 90 ? T.success : pct >= 70 ? T.warning : T.error;
              return (
                <div key={sq.split} className="p-2.5 rounded-lg border" style={{ borderColor: T.border }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold">Split {sq.split}</span>
                    <span className="text-[10px] font-bold" style={{ color: barColor }}>{pct}%</span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-1.5 rounded-full bg-gray-200 mb-1.5">
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: barColor }} />
                  </div>
                  <div className="grid grid-cols-3 gap-1 text-[8px]" style={{ color: T.textLight }}>
                    <div>
                      <div className="font-bold" style={{ color: T.text }}>{sq.detected}</div>
                      <div>detect.</div>
                    </div>
                    <div>
                      <div className="font-bold" style={{ color: T.success }}>{sq.recovered}</div>
                      <div>recup.</div>
                    </div>
                    <div>
                      <div className="font-bold" style={{ color: T.warning }}>{sq.estimated_missing}</div>
                      <div>est.falta</div>
                    </div>
                  </div>
                  {sq.critical_gaps > 0 && (
                    <div className="mt-1 text-[8px] font-bold" style={{ color: T.error }}>
                      ⚠ {sq.critical_gaps} gap{sq.critical_gaps > 1 ? 's' : ''} crítico{sq.critical_gaps > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Gap table */}
      <div className="px-5">
        <h4 className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: T.textLight }}>
          Detalhamento por Gap
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b" style={{ borderColor: T.border }}>
                <th className="text-left py-1.5 px-2 font-bold" style={{ color: T.textLight }}>ID</th>
                <th className="text-left py-1.5 px-2 font-bold" style={{ color: T.textLight }}>Lap</th>
                <th className="text-left py-1.5 px-2 font-bold" style={{ color: T.textLight }}>Sev.</th>
                <th className="text-right py-1.5 px-2 font-bold" style={{ color: T.textLight }}>Duração</th>
                <th className="text-right py-1.5 px-2 font-bold" style={{ color: T.textLight }}>Est. Falta</th>
                <th className="text-right py-1.5 px-2 font-bold" style={{ color: T.textLight }}>KP Conf</th>
                <th className="text-left py-1.5 px-2 font-bold" style={{ color: T.textLight }}>Razão</th>
                <th className="text-left py-1.5 px-2 font-bold" style={{ color: T.textLight }}>Recuperação</th>
              </tr>
            </thead>
            <tbody>
              {gaps.map((g, i) => (
                <tr key={g.gap_id || i} className="border-b hover:bg-gray-50" style={{ borderColor: '#f3f4f6' }}>
                  <td className="py-1.5 px-2 font-mono font-bold">{g.gap_id || `G${String(i + 1).padStart(3, '0')}`}</td>
                  <td className="py-1.5 px-2">{g.lap}</td>
                  <td className="py-1.5 px-2">
                    <span className="flex items-center gap-1">
                      <SeverityDot severity={g.severity} />
                      {g.severity === 'critical' ? 'Crítico' : 'Alerta'}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">{g.dt?.toFixed(2)}s</td>
                  <td className="py-1.5 px-2 text-right font-bold" style={{ color: T.warning }}>
                    {g.estimated_missing}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <span style={{
                      color: (g.kp_conf || 0) < 0.2 ? T.error : (g.kp_conf || 0) < 0.5 ? T.warning : T.success
                    }}>
                      {((g.kp_conf || 0) * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="py-1.5 px-2" style={{ color: T.textLight }}>
                    {g.reason === 'low_keypoint_visibility' ? '👁 Visib. baixa' :
                     g.reason === 'missing_strokes' ? '🏊 Braçadas faltando' : g.reason || '—'}
                  </td>
                  <td className="py-1.5 px-2">
                    <RecoveryBadge result={g.recovery_result} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Section 8: Auto-generated Observations
// ═══════════════════════════════════════════════════════════════════

const Observations = ({ session }) => {
  const observations = useMemo(() => {
    const obs = [];
    const hydro = session.hydroMetrics;
    const countQuality = session.countQuality;
    const raceDuration = session.raceDurationSeconds;
    const totalOfficial = session.totalTimeOfficial;
    const startToFirst = session.startToFirstStrokeS;
    const turnDetection = session.turnDetection;
    const insights = session.insights || [];
    const fatigue = session.fatigue || [];

    // Insights from pipeline
    insights.forEach(ins => {
      obs.push({
        type: ins.type === 'success' ? 'success' : ins.type === 'warning' ? 'warning' : 'info',
        title: ins.title,
        text: ins.description,
        priority: ins.priority,
      });
    });

    // Stroke count observations — single canonical source
    const displayCount = session.canonicalStrokeCount;
    const countLabel = `${displayCount} braçadas detectadas`;
    if (displayCount > 0 && countQuality === 'good') {
      obs.push({ type: 'success', title: '✓ Contagem confiável', text: `${countLabel} com alta confiança.` });
    } else if (countQuality === 'fair') {
      obs.push({ type: 'warning', title: '⚠ Contagem com ressalvas', text: `${countLabel} com confiança média — verificar visualmente.` });
    } else if (countQuality === 'poor') {
      obs.push({ type: 'error', title: '⚠ Contagem de baixa confiança', text: `${countLabel} mas o sinal é fraco — usar apenas como referência.` });
    }

    // Hydro observations
    if (hydro?.hydroEfficiencyScore != null) {
      const cls = getHydroClassification(hydro.hydroEfficiencyScore);
      if (hydro.hydroEfficiencyScore >= 85) {
        obs.push({ type: 'success', title: '🏆 Eficiência hidrodinâmica de elite', text: 'Posicionamento corporal no nível dos melhores — baixa oscilação, alinhamento horizontal e core estável.' });
      } else if (hydro.hydroEfficiencyScore < 50) {
        obs.push({ type: 'warning', title: '🔧 Eficiência hidrodinâmica baixa', text: 'Oportunidade de melhoria significativa no posicionamento corporal — focar em streamline e controle de core.' });
      }
    }

    // Fatigue observations
    fatigue.forEach(f => {
      if (f.degradationPct > 10) {
        obs.push({ type: 'warning', title: `📉 Degradação: ${f.metric}`, text: `${f.interpretation} — queda de ${f.degradationPct.toFixed(0)}% na segunda metade da prova.` });
      }
    });

    // Start observation
    if (startToFirst != null) {
      if (startToFirst < 1.0) {
        obs.push({ type: 'success', title: '⚡ Saída rápida', text: `Tempo até a primeira braçada: ${fmtTime(startToFirst)} — reação excelente.` });
      } else if (startToFirst > 2.5) {
        obs.push({ type: 'info', title: '🏊 Saída longa', text: `Tempo até a primeira braçada: ${fmtTime(startToFirst)} — verificar se o underwater foi eficiente.` });
      }
    }

    // Turn observation
    if (turnDetection && turnDetection.turnCount > 0 && turnDetection.avgQualityScore != null) {
      if (turnDetection.avgQualityScore >= 80) {
        obs.push({ type: 'success', title: '✓ Viradas de qualidade', text: `${turnDetection.turnCount} virada(s) com score médio ${turnDetection.avgQualityScore.toFixed(0)}/100.` });
      } else if (turnDetection.avgQualityScore < 60) {
        obs.push({ type: 'warning', title: '🔄 Viradas a melhorar', text: `Score médio das viradas: ${turnDetection.avgQualityScore.toFixed(0)}/100 — oportunidade de ganho.` });
      }
    }

    // Recommendations from pipeline
    (session.recommendations || []).forEach(rec => {
      obs.push({
        type: rec.priority === 'high' ? 'warning' : 'info',
        title: `💡 ${rec.area}`,
        text: rec.suggestion,
      });
    });

    // Sort: success last, warnings first
    const order = { error: 0, warning: 1, info: 2, success: 3 };
    obs.sort((a, b) => (order[a.type] ?? 2) - (order[b.type] ?? 2));

    return obs;
  }, [session]);

  if (observations.length === 0) {
    return (
      <div className="text-center py-4">
        <Lightbulb size={24} className="mx-auto mb-2 opacity-30" style={{ color: T.textLight }} />
        <p className="text-xs" style={{ color: T.textLight }}>Nenhuma observação automática gerada para esta análise.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {observations.map((obs, i) => {
        const bgColor = obs.type === 'success' ? T.successBg : obs.type === 'warning' ? T.warningBg : obs.type === 'error' ? T.errorBg : T.infoBg;
        const iconColor = obs.type === 'success' ? T.success : obs.type === 'warning' ? T.warning : obs.type === 'error' ? T.error : T.info;
        const Icon = obs.type === 'success' ? CheckCircle2 : obs.type === 'warning' ? AlertTriangle : obs.type === 'error' ? AlertTriangle : Info;
        return (
          <div key={i} className="flex items-start gap-2.5 py-2.5 px-3.5 rounded-lg" style={{ backgroundColor: bgColor }}>
            <Icon size={14} style={{ color: iconColor }} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold" style={{ color: T.text }}>{obs.title}</p>
              <p className="text-[11px] leading-relaxed mt-0.5" style={{ color: T.textLight }}>{obs.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Main Page Component
// ═══════════════════════════════════════════════════════════════════

const RaceResultPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [raceCtx, setRaceCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);

  // Fetch session data — normalize immediately to unify all fields
  const fetchSession = useCallback(async () => {
    try {
      const { data } = await getBiomechanicsSession(sessionId);
      const raw = data?.data || data;
      if (!raw) throw new Error('Sessão não encontrada');

      const normalized = normalizeSession(raw);
      setSession(normalized);
      setError(null);

      // If still processing, start polling
      if (normalized.isProcessing) {
        setPolling(true);
      } else {
        setPolling(false);
      }
    } catch (err) {
      setError(err.message || 'Erro ao carregar sessão');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Fetch race context
  const fetchRaceCtx = useCallback(async () => {
    try {
      const resp = await getRaceContext(sessionId);
      const d = resp.data?.data || resp.data;
      if (d?.linked) setRaceCtx(d.context);
    } catch { /* no race context */ }
  }, [sessionId]);

  // Initial load
  useEffect(() => {
    fetchSession();
    fetchRaceCtx();
  }, [fetchSession, fetchRaceCtx]);

  // Polling effect — when session completes, do FULL re-fetch for rich data
  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(async () => {
      try {
        const statusResult = await checkSessionStatus(sessionId);
        if (!statusResult) return;

        if (statusResult.status === 'completed' || statusResult.status === 'failed') {
          setPolling(false);
          // CRITICAL: Do a FULL re-fetch to get strokeTiming, hydroMetrics,
          // charts, and all rich data — checkSessionStatus only returns sparse fields.
          try {
            const { data } = await getBiomechanicsSession(sessionId);
            const fullSession = data?.data || data;
            if (fullSession) {
              setSession(normalizeSession(fullSession));
              console.log('[RaceResult] Full re-fetch on completion — all sections hydrated');
            }
          } catch (fetchErr) {
            console.warn('[RaceResult] Full re-fetch failed, using status data:', fetchErr);
            setSession(normalizeSession(statusResult));
          }
          // Also refresh race context (may have been linked during processing)
          fetchRaceCtx();
        } else {
          // Still processing — update ONLY the status field, keep all other normalized data
          setSession(prev => prev ? { ...prev, status: statusResult.status, isProcessing: true, isCompleted: false } : prev);
        }
      } catch { /* continue polling */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [polling, sessionId, fetchRaceCtx]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <div className="text-center">
          <RefreshCw size={32} className="animate-spin mx-auto mb-3" style={{ color: T.primary }} />
          <p className="text-sm font-bold" style={{ color: T.text }}>Carregando análise...</p>
          <p className="text-xs mt-1" style={{ color: T.textLight }}>Sessão #{sessionId}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <div className="text-center">
          <AlertTriangle size={32} className="mx-auto mb-3" style={{ color: T.error }} />
          <p className="text-sm font-bold" style={{ color: T.text }}>Erro ao carregar</p>
          <p className="text-xs mt-1" style={{ color: T.textLight }}>{error}</p>
          <button onClick={fetchSession} className="mt-3 px-4 py-2 text-xs font-bold rounded-lg"
            style={{ backgroundColor: T.primary, color: '#fff' }}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // Processing state — show what we have
  const isProcessing = session?.isProcessing;

  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }}>
      {/* Top navigation */}
      <div className="sticky top-0 z-30 border-b" style={{ backgroundColor: '#ffffffee', borderColor: T.border, backdropFilter: 'blur(8px)' }}>
        <div className="max-w-5xl mx-auto px-4 py-2 flex items-center gap-3">
          <button onClick={() => navigate('/swim-vision')} className="p-1.5 rounded-lg hover:bg-gray-100">
            <ArrowLeft size={16} style={{ color: T.text }} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-black truncate" style={{ color: T.text }}>
              Resultado da Análise — Sessão #{sessionId}
            </p>
          </div>
          {polling && (
            <Badge color={T.warning} bg={T.warningBg}>
              <RefreshCw size={10} className="animate-spin" /> Atualizando...
            </Badge>
          )}
          <Link to="/swim-vision/dashboard" className="text-[10px] font-bold px-2.5 py-1 rounded-md border hover:bg-gray-50"
            style={{ borderColor: T.border, color: T.textLight }}>
            Dashboard
          </Link>
        </div>

        {/* Section nav tabs — scrollable */}
        <div className="max-w-5xl mx-auto px-4 overflow-x-auto hide-scrollbar">
          <div className="flex gap-1 pb-1.5 min-w-max">
            {[
              { id: 'summary', label: 'Resumo', icon: Gauge },
              { id: 'splits', label: 'Parciais', icon: Layers },
              { id: 'chart', label: 'Gráfico', icon: BarChart3 },
              { id: 'video', label: 'Vídeo', icon: Play },
              { id: 'hydro', label: 'Hidro', icon: Waves },
              { id: 'technical', label: 'Técnico', icon: Settings },
              { id: 'observations', label: 'Obs.', icon: Lightbulb },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => document.getElementById(tab.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold whitespace-nowrap hover:bg-gray-100 transition-colors"
                style={{ color: T.textLight }}
              >
                <tab.icon size={11} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Page content */}
      <div className="max-w-5xl mx-auto px-4 py-5 space-y-5">

        {/* 1. Race Header */}
        <RaceHeader session={session} raceCtx={raceCtx} />

        {/* Processing banner */}
        {isProcessing && (
          <div className="rounded-xl p-4 flex items-center gap-3" style={{ backgroundColor: T.warningBg, border: `1px solid ${T.warning}30` }}>
            <RefreshCw size={18} className="animate-spin flex-shrink-0" style={{ color: T.warning }} />
            <div>
              <p className="text-sm font-bold" style={{ color: T.text }}>Análise em andamento...</p>
              <p className="text-xs mt-0.5" style={{ color: T.textLight }}>
                A página será atualizada automaticamente quando cada seção estiver pronta.
              </p>
            </div>
          </div>
        )}

        {/* 2. Race Summary */}
        <Section id="summary" title="Resumo Geral da Prova" icon={Gauge}>
          {session.canonicalStrokeCount > 0 || session.overall_score != null || session.strokeTiming ? (
            <RaceSummary session={session} />
          ) : isProcessing ? (
            <SectionSkeleton lines={2} />
          ) : (
            <Unavailable message="Dados de resumo indisponíveis" />
          )}
        </Section>

        {/* 3. Per-50m Lap Blocks */}
        <Section id="splits" title={`Parciais por ${session.poolLengthM || 50}m`} icon={Layers}
          badge={session.lapBlocks?.length > 0 ? (
            <Badge color={T.info} bg={T.infoBg}>{session.lapBlocks.length} blocos</Badge>
          ) : session.splitsOfficial?.length > 0 ? (
            <Badge color={T.success} bg={T.successBg}>Oficial</Badge>
          ) : null}>
          {session.isCompleted || session.lapBlocks?.length > 0 ? (
            <LapBlocks session={session} />
          ) : session.splitsOfficial?.length > 0 ? (
            <SplitsSection session={session} />
          ) : isProcessing ? (
            <SectionSkeleton />
          ) : (
            <Unavailable message="Parciais não disponíveis — vincule a prova ou processe um vídeo com ≥100m" />
          )}
        </Section>

        {/* 4. Main Chart */}
        <Section id="chart" title="Gráfico da Prova" icon={BarChart3}>
          {session.strokeTiming || session.canonicalStrokeCount > 0 ? (
            <MainChart session={session} />
          ) : isProcessing ? (
            <SectionSkeleton lines={4} />
          ) : (
            <Unavailable message="Dados insuficientes para gráfico" />
          )}
        </Section>

        {/* 5. Video + Timeline */}
        <Section id="video" title="Vídeo + Timeline" icon={Play}>
          <VideoTimeline session={session} />
        </Section>

        {/* 6. Hydrodynamic Efficiency */}
        <Section id="hydro" title="Eficiência Hidrodinâmica" icon={Waves}
          badge={session.hydroMetrics ? (
            <Badge color={T.hydro} bg={T.hydroBg}>
              {getHydroClassification(session.hydroMetrics?.hydroEfficiencyScore).label}
            </Badge>
          ) : null}>
          <HydroSection session={session} />
        </Section>

        {/* 7. Technical Details */}
        <Section id="technical" title="Detalhes Técnicos da Análise" icon={Settings}>
          <TechnicalDetails session={session} />
        </Section>

        {/* 7b. Gap Analysis Report (v3.12) */}
        {session.gapAnalysis && (
          <Section id="gaps" title="Relatório de Gaps" icon={AlertTriangle}
            badge={session.hasGapIssues ? (
              <Badge color={T.error} bg={T.errorBg}>
                {session.gapAnalysis.critical_gaps || 0} crítico{(session.gapAnalysis.critical_gaps || 0) !== 1 ? 's' : ''}
              </Badge>
            ) : (
              <Badge color={T.success} bg={T.successBg}>✓ sem gaps</Badge>
            )}>
            <GapReport session={session} />
          </Section>
        )}

        {/* 8. Observations */}
        <Section id="observations" title="Observações e Recomendações" icon={Lightbulb}>
          {session.status === 'completed' || session.overall_score != null ? (
            <Observations session={session} />
          ) : isProcessing ? (
            <SectionSkeleton />
          ) : (
            <Unavailable message="Observações disponíveis após conclusão da análise" />
          )}
        </Section>

        {/* Footer */}
        <div className="text-center py-4 space-y-2">
          <p className="text-[10px]" style={{ color: T.textMuted }}>
            Sessão #{sessionId} • {session.created_at ? new Date(session.created_at).toLocaleString('pt-BR') : '—'}
            {session.model_version && ` • ${session.model_version}`}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/swim-vision" className="text-[10px] font-bold px-3 py-1.5 rounded-md border hover:bg-gray-50"
              style={{ borderColor: T.border, color: T.textLight }}>
              ← Nova Análise
            </Link>
            <Link to="/swim-vision/dashboard" className="text-[10px] font-bold px-3 py-1.5 rounded-md border hover:bg-gray-50"
              style={{ borderColor: T.border, color: T.textLight }}>
              Dashboard Geral
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaceResultPage;
